<template>
  <u-popover
    :ui="popoverUi"
    :popper="{arrow: true, placement: 'bottom-end'}"
  >
    <u-button
      icon="i-ri-share-2-line"
      label="Share"
      color="charcoal"
      variant="outline"
      :size="size"
      title="Share Profile"
    />

    <template #panel="{close}">
      <div class="w-[min(22rem,calc(100vw-2rem))] p-4 text-left">
        <div class="flex items-start gap-3">
          <div class="min-w-0 flex-1">
            <h3 class="text-lg font-semibold text-charcoal-700">{{ shareHeading }}</h3>
            <p class="mt-1 text-sm text-charcoal-500">{{ shareDescription }}</p>
          </div>

          <u-button
            color="charcoal"
            variant="ghost"
            :icon="COMMON_ICONS.close"
            class="-mr-2 -mt-2 shrink-0"
            title="Close Profile sharing"
            @click="close"
          />
        </div>

        <div class="pb-1 pt-2">
          <div class="space-y-1">
            <u-button
              block
              color="charcoal"
              variant="ghost"
              icon="i-ri-link"
              :label="hasCopied ? 'Link Copied!' : 'Copy Link'"
              class="justify-start bg-charcoal-50 py-2.5 text-base hover:bg-charcoal-100 dark:bg-charcoal-700 dark:hover:bg-charcoal-600"
              @click="copyLink()"
            />
            <u-button
              v-if="canUseWebShare"
              block
              color="charcoal"
              variant="ghost"
              :icon="COMMON_ICONS.share"
              label="Share…"
              class="justify-start bg-charcoal-50 py-2.5 text-base hover:bg-charcoal-100 dark:bg-charcoal-700 dark:hover:bg-charcoal-600"
              @click="shareWithDevice(close)"
            />
            <u-button
              block
              color="charcoal"
              variant="ghost"
              icon="i-ri-qr-code-line"
              label="QR Code"
              class="justify-start bg-charcoal-50 py-2.5 text-base hover:bg-charcoal-100 dark:bg-charcoal-700 dark:hover:bg-charcoal-600"
              @click="openQrCode(close)"
            />
          </div>

          <div v-if="isCurrentUser" class="mt-2 border-t border-slate-200 pt-2">
            <button
              type="button"
              class="group flex w-full items-center gap-3 rounded-lg bg-heroblue-100/50 px-3 py-3 text-left text-heroblue-700 transition-colors hover:bg-heroblue-200/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heroblue-500"
              @click="inviteOthers(close)"
            >
              <span class="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/80">
                <u-icon :name="COMMON_ICONS.connection" class="size-5" aria-hidden="true" />
              </span>

              <span class="min-w-0 flex-1">
                <span class="block font-semibold leading-5">Invite Others to Connect</span>
                <span class="mt-0.5 block text-xs leading-4 text-heroblue-600">
                  Send a connection link or email invitation.
                </span>
              </span>

              <u-icon
                name="i-ri-arrow-right-line"
                class="size-5 shrink-0 transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>
    </template>
  </u-popover>
</template>

<script setup>
  import {CoreQrCodeModal} from '#components';
  import {waitFor} from '~/utils/flow-control.js';

  const props = defineProps({
    user: {
      type: Object,
      required: true
    },
    isCurrentUser: Boolean,
    size: {
      type: String,
      default: 'md'
    }
  });

  const requestUrl = useRequestURL();
  const canUseWebShare = ref(false);
  const hasCopied = ref(false);
  const {copy} = useClipboard({legacy: true});
  const gtm = useGtm();
  const shareHeading = computed(() => (
    props.isCurrentUser ? 'Share Your Profile' : `Share ${props.user.name}'s Profile`
  ));
  const shareDescription = computed(() => (
    props.isCurrentUser
      ? 'Share your profile with family and friends.'
      : `Help others discover ${props.user.name} and request a connection.`
  ));
  const profileUrl = computed(() => {
    const route = buildRouteParams(SHARE_ROUTES.profile, {
      userId: props.user.slug || props.user.id
    });
    return `${requestUrl.origin}${route}${buildQueryString({connect: 'true'})}`;
  });
  const popoverUi = {
    wrapper: 'inline-block',
    width: 'w-auto',
    background: 'bg-white dark:bg-charcoal-800',
    arrow: {
      background: 'before:bg-white dark:before:bg-charcoal-800'
    }
  };

  function trackShareAction(action) {
    gtm.trackEvent({
      event: `core_ui-profile_share-${action}`,
      action: 'click',
      category: 'profile'
    });
  }

  async function copyLink({trackClick = true} = {}) {
    if (trackClick) {
      trackShareAction('copy');
    }

    await usePermission('clipboard-read');
    await copy(profileUrl.value);
    hasCopied.value = true;
    await waitFor(4000);
    hasCopied.value = false;
  }

  async function shareWithDevice(close) {
    trackShareAction('native');

    try {
      await navigator.share({
        title: `Share ${props.user.name}'s Profile`,
        text: `View ${props.user.name}'s Profile on FOREVER and request a connection.`,
        url: profileUrl.value
      });
      close();
    } catch (error) {
      if (error.name !== 'AbortError') {
        await copyLink({trackClick: false});
      }
    }
  }

  function openQrCode(close) {
    trackShareAction('qrcode');
    close();
    useFModal({
      id: 'profile-share-qr-code',
      component: CoreQrCodeModal,
      showClose: false,
      size: 'xs',
      props: {
        filename: `${props.user.name}-profile`,
        url: profileUrl.value
      }
    }).open();
  }

  function inviteOthers(close) {
    close();
    useUserActions().sendInvites();
  }

  onMounted(() => {
    canUseWebShare.value = typeof navigator.share === 'function';
  });
</script>
